import { ClientData } from '../types';

export const sampleClientData: ClientData = {
  id: '60332340',
  name: 'Sample Client',
  slug: 'sample-client',
  headline: 'Understanding Your Core Audience',
  introduction: 'This comprehensive audience analysis reveals key insights about your most engaged customers. Our data shows a highly educated, affluent demographic with strong values alignment and distinct brand preferences that can inform your marketing strategy.',
  pagePasswordHash: null,
  demographics: {
    targetableIds: '90K',
    gender: {
      male: 73,
      female: 28
    },
    ageGroups: {
      '18-24': 20,
      '25-34': 18,
      '35-44': 18,
      '45-49': 27,
      '50-54': 27,
      '55-64': 27,
      '65+': 27
    },
    income: {
      '<$25k': 0,
      '$25-50k': 21,
      '$50-75k': 0,
      '$75-100k': 0,
      '$100-150k': 26,
      '$150-200k': 0,
      '$200-250k': 0,
      '$250k+': 0
    },
    employment: {
      fullTime: 46,
      fullTimeIndex: 119,
      selfEmployed: 0,
      selfEmployedIndex: 0,
      retired: 0,
      index: 119
    },
    education: {
      lessThanHighSchool: 0,
      highSchool: 0,
      someCollege: 28,
      collegeDegree: 17,
      postGrad: 49
    },
    maritalStatus: {
      single: 34,
      married: 61,
      divorced: 0,
      widowed: 0
    },
    children: {
      none: 84,
      one: 0,
      twoOrThree: 0,
      fourOrFive: 0,
      sixPlus: 0
    }
  },
  personalValues: [
    { name: 'Nature', value: 380, category: 'openness' },
    { name: 'Creativity', value: 361, category: 'openness' },
    { name: 'Stimulation', value: 331, category: 'openness' },
    { name: 'Independence', value: 212, category: 'openness' },
    { name: 'Pleasure', value: 84, category: 'openness' },
    { name: 'Equality', value: 226, category: 'self-transcendence' },
    { name: 'Caring', value: 144, category: 'self-transcendence' },
    { name: 'Tolerance', value: 120, category: 'self-transcendence' },
    { name: 'Conformity', value: 160, category: 'conservation' },
    { name: 'Safety', value: 171, category: 'conservation' },
    { name: 'Dependability', value: 112, category: 'conservation' }
  ],
  psychologicalDrivers: [
    { insight: 'Romantic Love', index: 176, composition: 28 },
    { insight: 'Living an Exciting Life', index: 160, composition: 37 },
    { insight: 'Proving Competence/Skills', index: 150, composition: 30 },
    { insight: 'Expressing Individuality', index: 138, composition: 24 },
    { insight: 'Creativity', index: 131, composition: 15 },
    { insight: 'Devotion to Faith', index: 128, composition: 30 },
    { insight: 'Trust From Others', index: 118, composition: 42 },
    { insight: 'Respect From Others', index: 110, composition: 15 }
  ],
  hobbiesAndHabits: [
    { insight: 'Bicycling', index: 286, composition: 22 },
    { insight: 'Lottery With Jackpot', index: 280, composition: 22 },
    { insight: 'Active Vacation', index: 237, composition: 20 },
    { insight: 'Casino: Couple Times Last 12 Months', index: 218, composition: 15 },
    { insight: 'Running or Jogging', index: 214, composition: 43 },
    { insight: 'Hiking/Camping', index: 206, composition: 32 },
    { insight: 'Lottery Negative Impact on Society', index: 192, composition: 28 },
    { insight: 'Attended Professional Sports Event', index: 179, composition: 27 }
  ],
  dailyRoutine: [
    { insight: 'Limit Calories/Fats/Salt', index: 136, composition: 57 },
    { insight: 'Life Grounded in Religion', index: 136, composition: 59 },
    { insight: 'Value Athletic Accomplishments', index: 133, composition: 34 },
    { insight: 'Prayer Important Part of Day', index: 124, composition: 62 },
    { insight: 'Participate in Teams/Classes', index: 114, composition: 15 },
    { insight: 'Physically Active Family', index: 112, composition: 47 },
    { insight: 'Keep Work and Life Separate', index: 112, composition: 72 },
    { insight: 'Exercise 2x or More Weekly', index: 111, composition: 67 }
  ],
  brandAffinity: {
    retailers: [
      { name: "Trader Joe's", index: 298, composition: 41 },
      { name: "Whole Foods", index: 277, composition: 35 },
      { name: "Ace Hardware", index: 225, composition: 30 },
      { name: "Safeway", index: 203, composition: 25 },
      { name: "Costco", index: 177, composition: 22 },
      { name: "Target", index: 174, composition: 20 }
    ],
    restaurants: [
      { name: "Red Robin", index: 788, composition: 22 },
      { name: "Other Fast Food", index: 561, composition: 18 },
      { name: "Jersey Mike's", index: 318, composition: 15 },
      { name: "Other Pizza", index: 316, composition: 14 },
      { name: "Other Casual", index: 297, composition: 12 },
      { name: "Panera Bread", index: 279, composition: 10 }
    ],
    hotels: [
      { name: "Other", index: 498, composition: 15 },
      { name: "Courtyard by Marriott", index: 338, composition: 12 },
      { name: "Hilton Hotels & Resorts", index: 243, composition: 10 },
      { name: "Marriott Hotels", index: 195, composition: 8 },
      { name: "Airbnb", index: 149, composition: 6 }
    ],
    airlines: [
      { name: "United Airlines", index: 268, composition: 12 },
      { name: "Southwest Airlines", index: 240, composition: 10 },
      { name: "Delta Air Lines", index: 214, composition: 9 },
      { name: "American Airlines", index: 163, composition: 7 }
    ],
    autoBrands: [
      { name: "Volkswagen", index: 1856, composition: 5 }
    ]
  },
  customCharts: [
    {
      title: "Purchase Intent by Category",
      type: "bar" as const,
      data: [
        { label: "Electronics", value: 85 },
        { label: "Clothing", value: 62 },
        { label: "Home & Garden", value: 74 },
        { label: "Sports", value: 48 },
        { label: "Books", value: 91 }
      ],
      description: "Likelihood to purchase in each category within the next 6 months"
    },
    {
      title: "Social Media Platform Usage",
      type: "pie" as const,
      data: [
        { label: "Instagram", value: 34 },
        { label: "Facebook", value: 28 },
        { label: "Twitter/X", value: 18 },
        { label: "LinkedIn", value: 15 },
        { label: "TikTok", value: 5 }
      ],
      description: "Primary social media platform for product discovery"
    }
  ],
  pageContent: {
    landing: {
      hero: {
        eyebrow: 'INSIGHTS OVERVIEW',
        title: 'Turning research into momentum',
        subtitle: 'Understand where your audience shows up, what they value, and how to move them to action.',
        description: 'Use these pages to align your brand, content, and digital strategy around the most compelling findings from the latest research.',
        backgroundImage: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=1800&q=80',
        ctaLabel: 'Explore Insights',
        ctaUrl: '#research'
      },
      keyInsight: {
        eyebrow: 'KEY INSIGHT',
        title: 'High-propensity audiences reward brands that help them live vibrant, values-aligned lives.',
        description: 'Your core audience indexes well-above average in curiosity and action—they seek brands that reinforce competence, confidence, and daily momentum.',
        supportingPoints: [
          { label: 'Audience Reach Advantage', value: '3.2x' },
          { label: 'Brand Affinity Lift', value: '+47%' },
          { label: 'Opportunity Window', value: '6 months' }
        ],
        backgroundImage: 'https://images.unsplash.com/photo-1518607692857-bff9babd9d73?auto=format&fit=crop&w=1800&q=80'
      },
      seoSummary: {
        title: 'SEO/AEO/GEO Insights',
        body: 'Competitive analysis shows share-of-voice pockets your brand can own with the right experiences. Start with the AI SEO overview to align on current sentiment and recommended actions.',
        ctaLabel: 'View SEO Strategy',
        ctaUrl: 'seo'
      },
      researchSummary: {
        title: 'Research Insights',
        body: 'Demographics, values, and behavioral journeys surface how this audience thinks and acts. Dive into the Research page for detailed charts and AI summaries you can put to immediate use.',
        ctaLabel: 'Explore Research',
        ctaUrl: 'research'
      }
    },
    seo: {
      hero: {
        eyebrow: 'AI SEO PLAYBOOK',
        title: 'Prioritize high-impact topics that balance visibility and sentiment.',
        subtitle: 'Visualize share-of-voice vs. sentiment, then capture the whitespace with a content plan built around audience intent.',
        backgroundImage: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=1800&q=80'
      },
      intro: {
        title: 'Where we win first',
        body: 'Build momentum around experiential and values-forward messaging. Audience language trends toward confidence, aspiration, and trustworthy expertise—especially where competitors underserve the intent.',
        bulletPoints: ['Reinforce proof of performance', 'Introduce momentum-driven stories', 'Highlight trust signals early in the experience']
      },
      contentTopics: [
        {
          id: 'topic-1',
          title: 'Confidence-building experiences',
          description: 'Frame services as accelerators that help audiences feel capable and ready to take action.',
          bullets: [
            'Own language around measurable results and professional credibility.',
            'Use testimonials and proof-points that remove hesitation.'
          ]
        },
        {
          id: 'topic-2',
          title: 'Values-aligned community impact',
          description: 'Connect brand stories to broader outcomes and belonging.',
          bullets: [
            'Highlight collaborative initiatives and shared success.',
            'Surface content formats that invite participation and storytelling.'
          ]
        }
      ],
      notes: 'Expand each topic with tiered assets: 1) Core landing experience, 2) Supportive resource hub, 3) Always-on contextual snippets.'
    },
    research: {
      hero: {
        eyebrow: 'AUDIENCE RESEARCH',
        title: 'Deep-dive into the people behind performance.',
        subtitle: 'Use the charts and AI summaries below to align messaging, media, and creative.'
      }
    }
  },
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
};
